import { DateRange, DataTable, ValueAccessor } from '../../models';
import { DefaultTimestampAccessor } from '../../models/Accessor';
import { relativeToDateRangeWindow } from './relativeToDateRangeWindow';
import { getLatestValue } from '../../engine';
import { DateUnit, getDateRange } from '../../dateUtils';
import { rangeBefore } from '../comparison/comparisonEngine';
import { applyWindow } from './integration';
import { DateWindow } from './integration/DateWindow';
import { DateWindowTypes } from './core';
import { ComparisonSpecType, isHistoricalComparisonSpec } from '../../models/comparisonSpec/ComparisonSpecType';
import { LatestDataDateWindow } from './types/latestDataDateWindow';

export const getWindowDateRange = (
  window: DateWindow,
  table: DataTable,
  accessor: ValueAccessor<Date> = DefaultTimestampAccessor
): DateRange | undefined => {
  switch (window.type) {
    case DateWindowTypes.CUSTOM:
      return window.range;
    case DateWindowTypes.LATEST_DATA:
      // Subtracting one because we would otherwise get a date range that is one unit too large
      const latestWindow = window as LatestDataDateWindow;
      return relativeToDateRangeWindow(
        table,
        { ...latestWindow.duration, numberOfUnits: latestWindow.duration.numberOfUnits - 1 },
        accessor
      );
    case DateWindowTypes.DEFINED_START:
      const latestRow = getLatestValue(table);
      if (!latestRow) {
        return undefined;
      }
      const last = accessor(latestRow);
      if (!last) {
        return undefined;
      }
      return { first: window.startDate, last: last as Date };
    case DateWindowTypes.LAST_N:
    case DateWindowTypes.SPECIAL:
    default:
      const windowedMetric = applyWindow(table, window, accessor);
      return getDateRange(windowedMetric, accessor);
  }
};

export const getWindowDateRangeForComparison = (
  dateWindow: DateWindow,
  table: DataTable,
  comparison?: ComparisonSpecType
): DateRange | undefined => {
  if (table.length === 0) {
    return undefined;
  }
  const windowDateRange = getWindowDateRange(dateWindow, table);
  if (windowDateRange && comparison && isHistoricalComparisonSpec(comparison)) {
    const comparisonRange = rangeBefore(
      windowDateRange,
      comparison.numberOfPeriods ?? 1,
      comparison.comparisonPeriod as DateUnit
    );
    return comparisonRange;
  }
  return undefined;
};
